import { cva } from 'class-variance-authority';

/**
 * Toast class recipes.
 *
 * Colour, geometry and type all come from the Alert's recipes and its `.ds-alert-*` slot classes:
 * the approved design is the Alert panel floated in a corner, so the two must not drift apart.
 * What lives here is only what floating adds — the fixed region, the stacking maths and the motion.
 *
 * ── Stacking ─────────────────────────────────────────────────────────────────────────────────────
 *
 * Every toast is absolutely positioned at the region's bottom edge and pushed up by the heights of
 * the toasts in front of it, which Base UI measures and hands over as `--toast-offset-y`, plus one
 * `x2` gap per step (`--toast-index`). Moving by `transform` rather than by layout is what lets a
 * toast leaving the stack slide the others down instead of making them jump.
 * The opt-in phone top placement mirrors the offset so older toasts stack downward.
 *
 * Newest sits nearest the corner. Beyond `limit` a toast is `data-limited`: hidden and not
 * clickable, and it slides into view when one in front leaves.
 */

export const toastViewportRecipe = cva(
  [
    // 400px is the set's fixed width — a dimension read off the drawing, not a step on the spacing scale.
    'fixed z-[1400] right-6 bottom-6 w-[400px]',
    // Phones: full width, since a 400px card does not fit a 390px viewport at all.
    'max-sm:right-4 max-sm:bottom-4 max-sm:left-4 max-sm:w-auto',
    'outline-none',
  ],
  {
    variants: {
      mobilePlacement: {
        top: 'max-sm:top-4 max-sm:bottom-auto',
      },
    },
  }
);

export const toastRootRecipe = cva(
  [
    'absolute right-0 bottom-0 left-0 box-border',
    '[--ds-toast-gap:--spacing(2)]',
    '[--ds-toast-y:calc(var(--toast-offset-y,0px)*-1_-_var(--toast-index,0)*var(--ds-toast-gap)_+_var(--toast-swipe-movement-y,0px))]',
    '[transform:translate(calc(var(--toast-swipe-movement-x,0px)_+_var(--ds-toast-shift-x,0px)),calc(var(--ds-toast-y)_+_var(--ds-toast-shift-y,0px)))]',

    // Enter from just below the corner; leave with a shorter nudge, faster. Surface roles, because a
    // toast is a surface arriving rather than an overlay opening from a trigger.
    'transition-[transform,opacity] duration-[var(--motion-surface)] ease-[var(--motion-surface-ease)]',
    'data-[ending-style]:duration-[var(--motion-surface-exit)] data-[ending-style]:ease-[var(--motion-surface-exit-ease)]',
    'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
    'motion-safe:data-[starting-style]:[--ds-toast-shift-y:--spacing(4)]',
    'motion-safe:data-[ending-style]:[--ds-toast-shift-y:--spacing(2)]',
    // A swipe that crosses the threshold carries on off the right edge instead of fading in place.
    'data-[ending-style]:data-[swipe-direction=right]:[--ds-toast-shift-x:calc(100%_+_var(--spacing)*6)]',
    'data-[swiping]:transition-none',
    // Reduced motion keeps the fade and drops every slide, including the stack re-settling.
    'motion-reduce:transition-opacity',

    'data-[limited]:invisible data-[limited]:opacity-0',
  ],
  {
    variants: {
      mobilePlacement: {
        top: 'max-sm:top-0 max-sm:bottom-auto max-sm:[--ds-toast-y:calc(var(--toast-offset-y,0px)_+_var(--toast-index,0)*var(--ds-toast-gap)_+_var(--toast-swipe-movement-y,0px))]',
      },
    },
  }
);

// `mt-1` on top of the content column's own `gap-1`: 8px above the button, as the set draws it.
export const toastActionsRecipe = cva(['mt-1 flex']);
